import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { getChannelTrace } from '../../services/api';
import { convertValue, getDisplayUnit, convertDistanceFt, getDistanceUnit, TOOLTIP_FONT_SCALES } from '../../utils/unitConvert';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, CartesianGrid,
  ReferenceDot, ReferenceArea,
} from 'recharts';
import { LAP_COLORS, getLapColor, TB_LAP_INDEX, TB_COLOR } from '../LapSelector/LapSelector';
import { publishExtent, clearExtent } from '../../utils/chartExtentBus';
import { publishSeriesPoints, clearSeriesPoints } from '../../utils/chartSeriesBus';
import { publishZoom, subscribeZoom } from '../../utils/chartZoomBus';
import { exportChartPng } from '../../utils/chartExport';
import { usePanOnWheel } from '../../hooks/usePanOnWheel';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import { useCornerHighlight } from '../../hooks/useCornerHighlight';
import styles from './SpeedTrace.module.css';
import { useTheme } from '../../contexts/ThemeContext';
import { lastCursor } from '../../utils/chartCursor';
import { formatLapTimestamp } from '../../utils/traceTime';
import { arrayExtent, arrayMin, arrayMax } from '../../utils/extent';

const M_TO_FT = 3.28084;

// Minimum pixel movement before a mousedown is promoted to a zoom-drag selection.
const DRAG_ARM_PX = 4;

const SPLIT_KEY = 'speedTraceSplit';

// Split mode: 1-px rule between stacked sub-charts, and the smallest sub-chart
// height that still leaves a usable plot area once margins + axis are taken out.
const SPLIT_DIVIDER_H = 1;
const MIN_SUB_H       = 60;

// Total right-side pixel width = margin.right + one 34-px YAxis per selected channel.
// Used everywhere we need to compute the plot area width from the DOM element width.
// (Mouse hover uses Recharts' activeCoordinate directly and doesn't need this.)
// In split mode every channel gets its own sub-chart with a left-hand axis, so
// nothing sits on the right but the bare margin — the plot area is wider, and every
// pixel↔dist_ft conversion below has to know that.
function rhs(nCh, split = false) {
  if (split) return 12;
  return (nCh === 2 ? 76 : nCh === 1 ? 44 : 12) + nCh * 34;
}

// Stroke styles cycled by channel position (0 = first selected, 1 = second).
// Overlay mode only: the dash is the only thing separating a channel from the speed
// line it shares a Y band with, so it stays long and near-opaque to read on the dark
// background. Split mode drops the dash entirely and draws these solid.
const CH_STROKES = [
  { strokeDasharray: '7 3', strokeOpacity: 0.9 },
  { strokeDasharray: '2 3', strokeOpacity: 0.8 },
];

function chDomain(vals) {
  const ext = arrayExtent(vals);
  if (!ext) return ['auto', 'auto'];
  const [lo, hi] = ext;
  if (lo === hi) { const p = Math.abs(lo) * 0.1 || 1; return [lo - p, hi + p]; }
  const pad = (hi - lo) * 0.05;
  return [lo - pad, hi + pad];
}

function fmtTick(v) {
  const a = Math.abs(v);
  if (a >= 10000) return `${(v / 1000).toFixed(0)}k`;
  if (a >= 1000)  return `${(v / 1000).toFixed(1)}k`;
  if (a >= 100)   return Math.round(v).toString();
  if (a >= 10)    return v.toFixed(1);
  return v.toFixed(2);
}

function fmtVal(v) {
  const a = Math.abs(v);
  if (a >= 10000) return `${(v / 1000).toFixed(1)}k`;
  if (a >= 1000)  return `${(v / 1000).toFixed(2)}k`;
  if (a >= 10)    return Math.round(v).toString();
  return v.toFixed(2);
}

function downsample(arr, maxPts) {
  if (arr.length <= maxPts) return arr;
  const step = arr.length / maxPts;
  return Array.from({ length: maxPts }, (_, i) => arr[Math.round(i * step)]);
}

// Find the point in a sorted-by-dist_ft array closest to distFt
function findNearest(points, distFt) {
  if (!points?.length) return null;
  let best = points[0];
  let bestDiff = Math.abs(points[0].dist_ft - distFt);
  for (let i = 1; i < points.length; i++) {
    const diff = Math.abs(points[i].dist_ft - distFt);
    if (diff < bestDiff) { best = points[i]; bestDiff = diff; }
  }
  return best;
}

// A series' driver source, derived from its key prefix ('s1-' = primary,
// 's2-{slot}-' = that compare-driver slot) — used to scope channel overlays
// and tooltip values to the correct driver's line.
function seriesSource(seriesKey) {
  if (seriesKey.startsWith('s2-')) {
    const rest = seriesKey.slice(3);
    return Number(rest.slice(0, rest.indexOf('-')));
  }
  return 'primary';
}

// Look up channel values at a given dist_ft; applies unit conversion for
// display. Only channels sourced from the same driver as `source` are
// considered, so a compare-driver's overlay never bleeds onto the primary
// series (or vice versa) when lap indices coincidentally match.
function channelValuesAt(chDataObj, selectedChs, lapIndex, distFt, unitPrefsObj, source) {
  const values = {}, units = {};
  for (const ch of selectedChs) {
    if (ch.source !== source) continue;
    const pts = chDataObj[ch.key]?.[lapIndex];
    if (pts?.length) {
      const nearest = findNearest(pts, distFt);
      if (nearest) {
        values[ch.key] = convertValue(nearest.value, ch.unit, unitPrefsObj || {});
        units[ch.key]  = getDisplayUnit(ch.unit, unitPrefsObj || {}) || ch.unit;
      }
    }
  }
  return { channelValues: values, channelUnits: units };
}

function buildSeries(lapTraces, selectedLaps, palette = LAP_COLORS, keyPrefix = '', zoomDomain = null, customColors = null, prefs = {}) {
  // Real laps consume palette slots; TB does not.
  let realCount = 0;
  return selectedLaps.map((lapIndex, i) => {
    const isTb     = lapIndex === TB_LAP_INDEX;
    const colorIdx = isTb ? 0 : (realCount++);
    const trace = lapTraces[lapIndex];
    if (!trace) return null;

    const raw = trace.points.filter(p =>
      p.dist_m != null && p.speed != null && p.lat && p.lon
    );
    if (!raw.length) return null;

    // When zoomed, filter to the visible range before downsampling — gives full
    // 600-point resolution within the zoom window instead of the whole lap.
    const source = zoomDomain
      ? raw.filter(p => {
          const ft = p.dist_m * M_TO_FT;
          return ft >= zoomDomain[0] && ft <= zoomDomain[1];
        })
      : raw;
    const sampled = downsample(source.length > 0 ? source : raw, 5000);
    const lapStartTime = trace.start_time ?? 0;
    const points  = sampled.map(p => ({
      dist_ft:    Math.round(p.dist_m * M_TO_FT),
      speed:      Math.round(convertValue(p.speed, 'mph', prefs) * 10) / 10,
      throttle:   p.throttle != null ? Math.round(p.throttle * 10) / 10 : null,
      rpm:        p.rpm      != null ? Math.round(p.rpm)               : null,
      lat_g:      p.lat_g   != null ? Math.round(p.lat_g * 1000) / 1000 : null,
      lon_g:      p.lon_g   != null ? Math.round(p.lon_g * 1000) / 1000 : null,
      video_time: p.video_time ?? null,
      lap_time:   p.time ?? null,
      csv_time:   lapStartTime + (p.time ?? 0),
      lat: p.lat,
      lon: p.lon,
    }));

    const maxPt = points.reduce((b, p) => p.speed > b.speed ? p : b, points[0]);
    const minPt = points.reduce((b, p) => p.speed < b.speed ? p : b, points[0]);

    const hasThrottle = raw.some(p => p.throttle != null);
    const hasRpm      = raw.some(p => p.rpm      != null);
    const hasLatG     = raw.some(p => p.lat_g    != null);
    const hasLonG     = raw.some(p => p.lon_g    != null);

    const minMaxFor = (key) => {
      const pts = points.filter(p => p[key] != null);
      if (!pts.length) return { max: null, min: null };
      return {
        max: pts.reduce((b, p) => p[key] > b[key] ? p : b, pts[0]),
        min: pts.reduce((b, p) => p[key] < b[key] ? p : b, pts[0]),
      };
    };

    const { max: maxThrPt, min: minThrPt } = minMaxFor('throttle');
    const { max: maxRpmPt, min: minRpmPt } = minMaxFor('rpm');
    const { max: maxLatGPt, min: minLatGPt } = minMaxFor('lat_g');
    const { max: maxLonGPt, min: minLonGPt } = minMaxFor('lon_g');

    return {
      lapIndex,
      isTb,
      seriesKey: `${keyPrefix}${lapIndex}`,
      colorIdx,
      lapLabel: isTb ? `TB ${trace.lap_time_str || ''}`.trim() : (trace.lap_time_str || `L${trace.lap_number}`),
      lapNumber: trace.lap_number,
      color: isTb ? TB_COLOR : ((customColors && customColors[lapIndex]) || palette[colorIdx % palette.length]),
      points, maxPt, minPt,
      maxThrPt, minThrPt, maxRpmPt, minRpmPt, maxLatGPt, minLatGPt, maxLonGPt, minLonGPt,
      hasThrottle, hasRpm, hasLatG, hasLonG,
    };
  }).filter(Boolean);
}

// ── Following dots — one per series at the hovered speed value ───────────────
// TOP_MARGIN and BOT_SPACE must mirror the LineChart margin + XAxis height.
const CHART_TOP    = 22;  // LineChart margin.top
const CHART_BOTTOM = 24;  // margin.bottom (4) + XAxis ticks/label (~20)

// `bands` maps a yAxisId → { top, plotH } for that signal's own plot area, measured
// from the top of the chart wrapper. Overlay mode passes nothing and everything shares
// the single full-height band; split mode passes one entry per sub-chart.
function HoverDots({ data, yMin, yMax, wrapHeight, channelDomains = {}, bands = null }) {
  if (!data?.entries?.length || wrapHeight == null) return null;
  const yRange  = yMax - yMin;
  const chartH  = wrapHeight - CHART_TOP - CHART_BOTTOM;
  if (yRange === 0 || (!bands && chartH <= 0)) return null;
  const bandFor = (id) => bands?.[id] ?? { top: CHART_TOP, plotH: chartH };

  const dots = [];
  for (const e of data.entries) {
    // Speed dot
    if (e.speed != null) {
      const band  = bandFor('speed');
      const yFrac = 1 - (e.speed - yMin) / yRange;
      const yPx   = band.top + yFrac * band.plotH;
      dots.push(
        <div
          key={`speed-${e.seriesKey}`}
          style={{
            position:      'absolute',
            left:          data.x - 5,
            top:           yPx - 5,
            width:         10,
            height:        10,
            borderRadius:  '50%',
            background:    e.color,
            border:        '2px solid #fff',
            pointerEvents: 'none',
            zIndex:        15,
            boxShadow:     `0 0 4px ${e.color}88`,
          }}
        />
      );
    }
    // Channel dots — smaller, slightly transparent
    if (e.channelValues) {
      for (const [chName, val] of Object.entries(e.channelValues)) {
        if (val == null) continue;
        const domain = channelDomains[chName];
        if (!domain) continue;
        const [lo, hi] = domain;
        const chRange = hi - lo;
        if (chRange === 0) continue;
        const band  = bandFor(chName);
        const yFrac = 1 - (val - lo) / chRange;
        const yPx   = band.top + yFrac * band.plotH;
        dots.push(
          <div
            key={`ch-${chName}-${e.seriesKey}`}
            style={{
              position:      'absolute',
              left:          data.x - 4,
              top:           yPx - 4,
              width:         8,
              height:        8,
              borderRadius:  '50%',
              background:    e.color,
              border:        '1.5px solid #fff',
              pointerEvents: 'none',
              zIndex:        14,
              boxShadow:     `0 0 3px ${e.color}88`,
              opacity:       0.8,
            }}
          />
        );
      }
    }
  }

  return <>{dots}</>;
}

// ── Imperative hover tooltip ──────────────────────────────────────────────────
function HoverTooltip({ data, wrapWidth, anchoredTooltip, unitPrefs = {} }) {
  if (!data?.entries?.length) return null;
  const { x, distFt, entries, side } = data;
  const speedUnit = getDisplayUnit('mph', unitPrefs);
  const scale = TOOLTIP_FONT_SCALES[unitPrefs.tooltipFontScale] ?? 1;
  const lapTime = entries.find(entry => Number.isFinite(entry.lap_time))?.lap_time ?? null;
  const lapTimestamp = formatLapTimestamp(lapTime);

  const TOOLTIP_W = Math.round(260 * scale);
  let leftVal, rightVal;
  if (anchoredTooltip) {
    leftVal  = side === 'left'  ? 50 : undefined;
    rightVal = side === 'right' ? 10 : undefined;
  } else {
    const flipLeft = x > (wrapWidth ?? 600) * 0.62;
    leftVal  = flipLeft ? undefined : x + 14;
    rightVal = flipLeft ? (wrapWidth - x + 8) : undefined;
  }

  return (
    <div
      className={styles.tooltip}
      style={{
        position:      'absolute',
        top:           10,
        left:          leftVal,
        right:         rightVal,
        pointerEvents: 'none',
        zIndex:        20,
        maxWidth:      TOOLTIP_W,
        fontSize:      `${(0.78 * scale).toFixed(3)}rem`,
      }}
    >
      <div
        className={styles.tooltipDist}
        title={lapTimestamp ? 'Elapsed lap time for the first listed trace' : undefined}
      >
        {(convertDistanceFt(distFt, unitPrefs) / 1000).toFixed(2)}k {getDistanceUnit(unitPrefs)}
        {lapTimestamp && <> · Lap {lapTimestamp}</>}
      </div>

      {entries.map((e, i) => {
        const refSpeed = entries[0].speed;
        const delta    = i > 0 && refSpeed != null && e.speed != null
          ? e.speed - refSpeed : null;

        return (
          <div key={e.seriesKey} className={styles.tooltipRow} style={{ borderLeftColor: e.color }}>
            <span className={styles.tooltipLabel}>{e.lapLabel}</span>

            {e.speed != null && (
              <span className={styles.tooltipVal}>
                {e.speed}<span className={styles.tooltipUnit}> {speedUnit}</span>
              </span>
            )}

            {delta != null && (
              <span
                className={styles.tooltipDelta}
                style={{ color: delta > 0 ? '#44cc88' : delta < 0 ? '#e8411a' : '#555' }}
              >
                {delta > 0 ? '+' : ''}{delta.toFixed(1)}
              </span>
            )}

            {e.channelValues && Object.entries(e.channelValues).map(([chName, val]) => (
              val != null ? (
                <span key={chName} className={styles.tooltipThr}>
                  {fmtVal(val)}
                  {e.channelUnits?.[chName] && (
                    <span className={styles.tooltipUnit}> {e.channelUnits[chName]}</span>
                  )}
                </span>
              ) : null
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── Channel picker dropdown ───────────────────────────────────────────────────
function SpeedChannelPicker({ availableChannels, selectedChannels, onToggle }) {
  const [open,   setOpen]   = useState(false);
  const [filter, setFilter] = useState('');
  const ref      = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      setFilter('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const count   = selectedChannels.length;
  const needle  = filter.trim().toLowerCase();
  const visible = needle
    ? availableChannels.filter(ch => ch.name.toLowerCase().includes(needle))
    : availableChannels;

  return (
    <div className={styles.chPicker} ref={ref}>
      <button
        className={`${styles.optBtn} ${count > 0 ? styles.optActive : ''}`}
        onClick={() => setOpen(v => !v)}
        title="Select channels to overlay"
      >
        Channels{count > 0 ? ` (${count})` : ''} ▾
      </button>
      {open && (
        <div className={styles.chDropdown}>
          <div className={styles.chDropdownHdr}>Select up to 2 channels</div>
          <div className={styles.chFilterWrap}>
            <input
              ref={inputRef}
              className={styles.chFilter}
              type="text"
              placeholder="Filter channels…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && (filter ? setFilter('') : setOpen(false))}
            />
          </div>
          <div className={styles.chList}>
            {visible.length === 0 && (
              <div className={styles.chNoMatch}>No channels match</div>
            )}
            {visible.map(ch => {
              const checked  = selectedChannels.some(c => c.key === ch.key);
              const disabled = !checked && count >= 2;
              return (
                <label
                  key={ch.key}
                  className={`${styles.chItem} ${disabled ? styles.chItemDisabled : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => onToggle(ch)}
                  />
                  <span className={styles.chItemName}>{ch.name}</span>
                  {ch.unit && <span className={styles.chItemUnit}>{ch.unit}</span>}
                  {ch.source !== 'primary' && <span className={styles.chDriverTag}>{ch.driverLabel}</span>}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function SpeedTrace({
  lapTraces, selectedLaps, height,
  compareDrivers = [],
  customColors1 = null,
  sectors = null, onSectorsChange = null,
  showSectorBands = false,
  anchoredTooltip = false,
  sessionId = null, availableChannels = [],
  unitPrefs = {},
  trackName = null, sessionDate = null, sessionVehicle = null, driverName1 = 'Driver 1',
}) {
  const { chartColors } = useTheme();
  const [showTopSpeed, setShowTopSpeed] = useState(true);
  const cornerHl = useCornerHighlight();
  const [selectedChannels, setSelectedChannels] = useState([]); // [{name, unit, source, key}]
  const [channelData,      setChannelData]      = useState({}); // {chKey: {lapIdx: [{dist_ft,value}]}}
  const [tooltipData,  setTooltipData]  = useState(null);
  const [wrapHeight,   setWrapHeight]   = useState(null);
  const [wrapWidth,    setWrapWidth]    = useState(0);
  const [wrapPadTop,   setWrapPadTop]   = useState(0);
  const [zoomDomain,    setZoomDomain]    = useState(null);
  // Channels stacked under the speed chart instead of overlaid on it. Persisted
  // because analysis panels unmount whenever another panel is maximized, which
  // would otherwise reset the choice several times a session.
  const [splitMode, setSplitMode] = useState(() => {
    try { return localStorage.getItem(SPLIT_KEY) === '1'; } catch { return false; }
  });
  // Split only means anything once there's a channel to split out.
  const isSplit = splitMode && selectedChannels.length > 0;
  const [isSelecting,   setIsSelecting]   = useState(false);
  const [selectionViz,  setSelectionViz]  = useState(null);
  const [isAnnotateMode, setIsAnnotateMode] = useState(false);
  const [showSectorEditor,  setShowSectorEditor]  = useState(false);
  const [editingSectorId,   setEditingSectorId]   = useState(null);

  // ── Split layout ──────────────────────────────────────────────────────────
  // Speed on top, then one sub-chart per selected channel. Every sub-chart keeps
  // the same margins and a 20-px XAxis slot (transparent on all but the bottom
  // one), so all plot areas start and end at the same pixel and the shared cursor
  // line is meaningful across them. Heights sum to exactly the wrapper height.
  // Computed here, ahead of the pixel↔dist_ft helpers, because they need to know
  // whether the split actually took effect — not merely that it was asked for.
  const splitLayout = useMemo(() => {
    if (!isSplit || !wrapHeight) return null;
    const n = selectedChannels.length;
    const usable = wrapHeight - n * SPLIT_DIVIDER_H;
    if (usable < MIN_SUB_H * (n + 1)) return null;   // too short to split usefully
    const chH    = Math.max(MIN_SUB_H, Math.floor(usable * (n === 2 ? 0.30 : 0.50)));
    const speedH = Math.max(MIN_SUB_H, usable - n * chH);
    // Plot-area bands (wrapper-relative) for the hover dots, one per yAxisId.
    const bands = {};
    let top = wrapPadTop;
    bands.speed = { top: top + CHART_TOP, plotH: speedH - CHART_TOP - CHART_BOTTOM };
    top += speedH + SPLIT_DIVIDER_H;
    for (const ch of selectedChannels) {
      bands[ch.key] = { top: top + CHART_TOP, plotH: chH - CHART_TOP - CHART_BOTTOM };
      top += chH + SPLIT_DIVIDER_H;
    }
    return { speedH, chH, bands };
  }, [isSplit, wrapHeight, wrapPadTop, selectedChannels]);

  // A panel too short to split falls back to the overlay, so every geometry
  // helper below keys off this and not off `isSplit`.
  const splitActive = !!splitLayout;

  // Overlay mode: every signal shares the one plot area.
  const overlayBands = useMemo(() => {
    if (!wrapHeight) return null;
    const band = { top: wrapPadTop + CHART_TOP, plotH: wrapHeight - CHART_TOP - CHART_BOTTOM };
    const bands = { speed: band };
    for (const ch of selectedChannels) bands[ch.key] = band;
    return bands;
  }, [wrapHeight, wrapPadTop, selectedChannels]);

  // Imperative lines — hover (yellow) and video playhead (orange)
  const chartWrapRef    = useRef(null);
  const hoverLineRef    = useRef(null);
  const playheadLineRef = useRef(null);
  const seriesRef       = useRef([]);
  const selectedChannelsRef = useRef([]);
  const splitActiveRef      = useRef(false);
  const channelDataRef      = useRef({});
  const unitPrefsRef        = useRef({});
  const zoomDomainRef   = useRef(null);
  const isSelectingRef   = useRef(false);
  const isAnnotateModeRef = useRef(false);
  const isDirectHoverRef = useRef(false);
  const tooltipSideRef   = useRef('right');
  const refAreaLeft      = useRef(null);
  const refAreaRight     = useRef(null);
  const finalizeZoomRef  = useRef(null);
  // Mousedown recorded here but not yet promoted to a real drag-selection —
  // see handleMouseDown/handleMouseMove. { x: distFt, clientX }
  const pendingDragRef   = useRef(null);

  const series1 = useMemo(
    () => buildSeries(lapTraces, selectedLaps, LAP_COLORS, 's1-', zoomDomain, customColors1, unitPrefs),
    [lapTraces, selectedLaps, zoomDomain, customColors1, unitPrefs]
  );
  const series2 = useMemo(
    () => compareDrivers.flatMap(d =>
      buildSeries(d.lapTraces, d.selectedLaps, d.palette, `s2-${d.slot}-`, zoomDomain, d.customColors, unitPrefs)),
    [compareDrivers, zoomDomain, unitPrefs]
  );
  const series = useMemo(() => [...series1, ...series2], [series1, series2]);

  useEffect(() => { seriesRef.current           = series; },          [series]);
  useEffect(() => { selectedChannelsRef.current = selectedChannels; }, [selectedChannels]);
  useEffect(() => { splitActiveRef.current = splitActive; }, [splitActive]);
  useEffect(() => { channelDataRef.current  = channelData; }, [channelData]);
  useEffect(() => { unitPrefsRef.current    = unitPrefs;   }, [unitPrefs]);

  // Fetch channel_trace data for every selected channel × every active lap of
  // its own driver — a primary-sourced channel overlays the primary session's
  // selectedLaps, a slot-sourced channel overlays that compare driver's own
  // selectedLaps against that driver's own session.
  useEffect(() => {
    if (!selectedChannels.length) return;
    for (const ch of selectedChannels) {
      const driver = ch.source === 'primary' ? null : compareDrivers.find(d => d.slot === ch.source);
      const sid  = ch.source === 'primary' ? sessionId : driver?.sessionId;
      const laps = ch.source === 'primary' ? selectedLaps : (driver?.selectedLaps || []);
      if (!sid) continue;
      for (const idx of laps) {
        if (channelDataRef.current[ch.key]?.[idx] !== undefined) continue;
        getChannelTrace(sid, idx, ch.name)
          .then(trace => {
            const pts = (trace.points || [])
              .filter(p => p.dist_m != null && p.channel_value != null)
              .map(p => ({ dist_ft: Math.round(p.dist_m * M_TO_FT), value: p.channel_value }));
            setChannelData(prev => ({
              ...prev,
              [ch.key]: { ...(prev[ch.key] || {}), [idx]: pts },
            }));
          })
          .catch(() => {
            setChannelData(prev => ({
              ...prev,
              [ch.key]: { ...(prev[ch.key] || {}), [idx]: [] },
            }));
          });
      }
    }
  }, [selectedChannels, selectedLaps, sessionId, compareDrivers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Natural (full-lap) extent — computed from raw lapTraces WITHOUT zoom filtering
  // so it stays stable while the user pans/zooms. Used for pan clamping and as
  // the x-extent broadcast to GTrace/ChannelTrace so their axes always align.
  const naturalDistsFt = useMemo(() => {
    const dists = [];
    for (const lapIndex of selectedLaps) {
      const trace = lapTraces[lapIndex];
      if (!trace) continue;
      trace.points.forEach(p => {
        if (p.dist_m != null && p.speed != null && p.lat && p.lon)
          dists.push(Math.round(p.dist_m * M_TO_FT));
      });
    }
    for (const d of compareDrivers) {
      for (const lapIndex of d.selectedLaps) {
        const trace = d.lapTraces[lapIndex];
        if (!trace) continue;
        trace.points.forEach(p => {
          if (p.dist_m != null && p.speed != null && p.lat && p.lon)
            dists.push(Math.round(p.dist_m * M_TO_FT));
        });
      }
    }
    return arrayExtent(dists);
  }, [lapTraces, selectedLaps, compareDrivers]);

  useEffect(() => {
    if (naturalDistsFt) {
      naturalExtentRef.current = naturalDistsFt;
      publishExtent(naturalDistsFt[0], naturalDistsFt[1]);
    } else {
      naturalExtentRef.current = null;
      clearExtent();
    }
  }, [naturalDistsFt]);

  // Publish primary series points for keyboard navigation and clear stale series.
  // This effect still depends on [series] because points change with zoom resolution.
  useEffect(() => {
    const primary = series[0];
    if (primary?.points?.length) {
      publishSeriesPoints(primary.points.map(p => ({
        distFt:    p.dist_ft,
        lat:       p.lat       ?? null,
        lon:       p.lon       ?? null,
        videoTime: p.csv_time ?? null,
      })));
    } else {
      clearSeriesPoints();
    }
  }, [series]);

  // Sync zoom with the shared bus (handles both initial mount and cross-chart zoom events).
  useEffect(() => {
    return subscribeZoom(domain => {
      setZoomDomain(domain);
      zoomDomainRef.current = domain;
    });
  }, []);

  // Listen for CommentsPanel toggling annotate mode
  useEffect(() => {
    const handler = (e) => {
      const active = !!e.detail?.active;
      setIsAnnotateMode(active);
      isAnnotateModeRef.current = active;
    };
    window.addEventListener('trackapp:annotate-mode', handler);
    return () => window.removeEventListener('trackapp:annotate-mode', handler);
  }, []);

  // Global mouseup — finalizes zoom drag even when mouse leaves chart
  useEffect(() => {
    const handler = () => finalizeZoomRef.current?.();
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, []);

  // Unified touch handler:
  //   vertical drag   → page scroll (pass through)
  //   horizontal drag → crosshair tooltip (same as mouse hover)
  //   long press+drag → zoom selection
  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;

    const LONG_PRESS_MS  = 400;
    const LOCK_THRESHOLD = 8; // px to determine scroll vs. chart interaction

    let timer         = null;
    let mode          = null; // null | 'crosshair' | 'zoom' | 'scroll' | 'pan'
    let startX        = 0;
    let startY        = 0;
    let panPrevX      = 0;
    let pinchPrevDist = 0;
    let tapEligible   = false; // single finger down, no mode locked yet

    // ── Crosshair helpers ───────────────────────────────────────────────────
    const updateCrosshair = (clientX) => {
      const distFt = pixelToDistFtRef.current(clientX);
      if (distFt == null) return;

      const rect     = wrap.getBoundingClientRect();
      const leftPad  = 34;
      const rightPad = rhs(selectedChannelsRef.current.length);
      const chartW   = wrap.clientWidth - leftPad - rightPad;
      const xPx      = leftPad + Math.max(0, Math.min(chartW, clientX - rect.left - leftPad));

      const line = hoverLineRef.current;
      if (line) { line.style.left = `${xPx}px`; line.style.display = 'block'; }

      const entries = seriesRef.current.map(s => {
        const pt = findNearest(s.points, distFt);
        if (!pt) return null;
        return {
          seriesKey: s.seriesKey, lapLabel: s.lapLabel, color: s.color,
          speed: pt.speed, lat: pt.lat, lon: pt.lon, video_time: pt.video_time,
          lap_time: pt.lap_time, csv_time: pt.csv_time,
          ...channelValuesAt(channelDataRef.current, selectedChannelsRef.current, s.lapIndex, distFt, unitPrefsRef.current, seriesSource(s.seriesKey)),
        };
      }).filter(Boolean);

      setTooltipData(entries.length ? { x: xPx, distFt, entries } : null);

      const first  = entries.find(e => !e.seriesKey.startsWith('s2-')) || entries[0];
      const second = entries.find(e => e.seriesKey.startsWith('s2-'));
      if (first?.lat && first?.lon || second?.lat && second?.lon) {
        window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
          detail: {
            lat: first?.lat, lon: first?.lon,
            lat2: second?.lat, lon2: second?.lon,
            distFt, videoTime: first?.csv_time ?? null,
          },
        }));
      }
    };

    const clearCrosshair = () => {
      isDirectHoverRef.current = false;
      setTooltipData(null);
      const line = hoverLineRef.current;
      if (line) line.style.display = 'none';
      window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail: null }));
    };

    // ── Zoom helpers ────────────────────────────────────────────────────────
    const armZoom = (clientX) => {
      wrap.style.touchAction = 'none';
      const distFt = pixelToDistFtRef.current(clientX);
      if (distFt == null) return;
      refAreaLeft.current    = distFt;
      refAreaRight.current   = distFt;
      isSelectingRef.current = true;
      setIsSelecting(true);
      setSelectionViz({ left: distFt, right: distFt });
    };

    // ── Event handlers ──────────────────────────────────────────────────────
    const onTouchStart = (e) => {
      // Two-finger touch → pan + pinch zoom
      if (e.touches.length === 2) {
        clearTimeout(timer);
        timer         = null;
        tapEligible   = false;
        mode          = 'pan';
        panPrevX      = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        pinchPrevDist = Math.hypot(
          e.touches[1].clientX - e.touches[0].clientX,
          e.touches[1].clientY - e.touches[0].clientY,
        );
        wrap.style.touchAction = 'none';
        return;
      }
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      mode   = null;
      // A stationary tap anchors the crosshair — but not on controls.
      tapEligible = !e.target?.closest?.('button, input, select, a');
      timer  = setTimeout(() => {
        mode = 'zoom';
        tapEligible = false;
        armZoom(startX);
      }, LONG_PRESS_MS);
    };

    const onTouchMove = (e) => {
      // Two-finger pan + pinch zoom
      if (mode === 'pan') {
        if (e.touches.length < 2) return;
        e.preventDefault();
        const currMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const currDist = Math.hypot(
          e.touches[1].clientX - e.touches[0].clientX,
          e.touches[1].clientY - e.touches[0].clientY,
        );
        const prevFt = pixelToDistFtRef.current(panPrevX);
        const currFt = pixelToDistFtRef.current(currMidX);
        if (prevFt != null && currFt != null) {
          onPanFtRef.current(-(currFt - prevFt));
        }
        if (pinchPrevDist > 0) {
          const scaleFactor = currDist / pinchPrevDist;
          const centerFt    = pixelToDistFtRef.current(currMidX);
          onPinchZoomRef.current(scaleFactor, centerFt);
        }
        panPrevX      = currMidX;
        pinchPrevDist = currDist;
        return;
      }

      const touch = e.touches[0];

      // Direction not yet determined — check if we've moved enough to decide
      if (mode === null) {
        const dx = Math.abs(touch.clientX - startX);
        const dy = Math.abs(touch.clientY - startY);
        if (dx < LOCK_THRESHOLD && dy < LOCK_THRESHOLD) return;
        clearTimeout(timer);
        timer = null;
        tapEligible = false;
        if (dx >= dy) {
          mode = 'crosshair';
          isDirectHoverRef.current = true;
          wrap.style.touchAction = 'none';
        } else {
          mode = 'scroll'; // let browser handle vertical scroll
        }
      }

      if (mode === 'zoom') {
        e.preventDefault();
        const distFt = pixelToDistFtRef.current(touch.clientX);
        if (distFt == null) return;
        refAreaRight.current = distFt;
        setSelectionViz({ left: refAreaLeft.current, right: distFt });
      } else if (mode === 'crosshair') {
        e.preventDefault();
        updateCrosshair(touch.clientX);
      }
      // 'scroll': fall through — browser scrolls the page normally
    };

    const onTouchEnd = () => {
      clearTimeout(timer);
      timer = null;
      const endMode = mode;
      const wasTap  = tapEligible;
      mode = null;
      tapEligible = false;
      wrap.style.touchAction = '';

      if (endMode === 'zoom') {
        finalizeZoomRef.current?.();
      } else if (endMode === 'crosshair') {
        // Keep the crosshair where the drag ended: on touch there is no
        // "mouse leave", and the anchored point carries to other panels
        // (they seed from the cursor cache when they mount).
        isDirectHoverRef.current = false;
      } else if (endMode === null && wasTap) {
        // Stationary tap → anchor the crosshair at the tapped point.
        updateCrosshair(startX);
      }
    };

    const onTouchCancel = () => {
      clearTimeout(timer);
      timer = null;
      if (mode === 'zoom') {
        isSelectingRef.current = false;
        setIsSelecting(false);
        setSelectionViz(null);
        refAreaLeft.current  = null;
        refAreaRight.current = null;
        wrap.style.touchAction = '';
      } else if (mode === 'crosshair') {
        clearCrosshair();
        wrap.style.touchAction = '';
      }
      mode = null;
    };

    wrap.addEventListener('touchstart',  onTouchStart,  { passive: true });
    wrap.addEventListener('touchmove',   onTouchMove,   { passive: false });
    wrap.addEventListener('touchend',    onTouchEnd);
    wrap.addEventListener('touchcancel', onTouchCancel);
    return () => {
      wrap.removeEventListener('touchstart',  onTouchStart);
      wrap.removeEventListener('touchmove',   onTouchMove);
      wrap.removeEventListener('touchend',    onTouchEnd);
      wrap.removeEventListener('touchcancel', onTouchCancel);
      clearTimeout(timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track chart wrapper size — height for dot y-positioning, width because the
  // split-mode sub-charts take explicit pixel dimensions rather than a
  // ResponsiveContainer (which mis-measures inside a flex column at RO fire time).
  // Both are *content-box* sizes: the charts lay out inside the wrapper's padding,
  // while the absolutely-positioned hover dots are placed against its padding box,
  // so the padding offset has to be carried separately or every dot sits high.
  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const measure = () => {
      const cs = getComputedStyle(wrap);
      const padT = parseFloat(cs.paddingTop)    || 0;
      const padB = parseFloat(cs.paddingBottom) || 0;
      const padL = parseFloat(cs.paddingLeft)   || 0;
      const padR = parseFloat(cs.paddingRight)  || 0;
      setWrapPadTop(padT);
      setWrapHeight(Math.max(0, wrap.clientHeight - padT - padB));
      setWrapWidth(Math.max(0, wrap.clientWidth - padL - padR));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    measure();
    return () => ro.disconnect();
  }, []);

  // Map hover → move DOM line + show tooltip when another chart is hovered
  useEffect(() => {
    const handler = (e) => {
      const line = hoverLineRef.current;
      const wrap = chartWrapRef.current;
      if (!line || !wrap) return;

      const distFt = e.detail?.distFt;
      if (distFt == null) {
        line.style.display = 'none';
        if (!isDirectHoverRef.current) setTooltipData(null);
        return;
      }

      const zoom = zoomDomainRef.current;
      if (zoom && (distFt < zoom[0] || distFt > zoom[1])) {
        line.style.display = 'none';
        if (!isDirectHoverRef.current) setTooltipData(null);
        return;
      }

      const allDists = seriesRef.current.flatMap(s => s.points.map(p => p.dist_ft));
      if (!allDists.length) { line.style.display = 'none'; return; }
      const xMin = zoom ? zoom[0] : arrayMin(allDists);
      const xMax = zoom ? zoom[1] : arrayMax(allDists);
      if (xMax === xMin) { line.style.display = 'none'; return; }

      const rightPx = rhs(selectedChannelsRef.current.length, splitActiveRef.current);
      const chartW  = wrap.clientWidth - 34 - rightPx;

      const t   = Math.max(0, Math.min(1, (distFt - xMin) / (xMax - xMin)));
      const xPx = 34 + t * chartW;
      line.style.left    = `${xPx}px`;
      line.style.display = 'block';

      if (!isDirectHoverRef.current) {
        const entries = seriesRef.current.map(s => {
          const pt = findNearest(s.points, distFt);
          if (!pt) return null;
          return {
            seriesKey: s.seriesKey, lapLabel: s.lapLabel, color: s.color,
            speed: pt.speed, lat: pt.lat, lon: pt.lon, video_time: pt.video_time,
            lap_time: pt.lap_time, csv_time: pt.csv_time,
            ...channelValuesAt(channelDataRef.current, selectedChannelsRef.current, s.lapIndex, distFt, unitPrefsRef.current, seriesSource(s.seriesKey)),
          };
        }).filter(Boolean);
        setTooltipData(entries.length ? { x: xPx, distFt, entries } : null);
      }
    };

    window.addEventListener('trackapp:map-hover', handler);
    return () => window.removeEventListener('trackapp:map-hover', handler);
  }, []);

  // Direct chart-hover listener — shows dot/tooltip when another chart is hovered.
  // TrackMap does not relay chart-hover as map-hover, so we listen here directly.
  // Keyboard-nav events (fromKeyboard: true) bypass the direct-hover guard so
  // arrow keys always move the crosshair even if the mouse is resting on the chart.
  useEffect(() => {
    const handler = (e) => {
      if (isDirectHoverRef.current && !e.detail?.fromKeyboard) return;

      const distFt = e.detail?.distFt;
      const line   = hoverLineRef.current;
      const wrap   = chartWrapRef.current;

      if (distFt == null) {
        setTooltipData(null);
        if (line) line.style.display = 'none';
        return;
      }
      if (!wrap) return;

      const zoom = zoomDomainRef.current;
      if (zoom && (distFt < zoom[0] || distFt > zoom[1])) {
        setTooltipData(null);
        if (line) line.style.display = 'none';
        return;
      }

      const allDists = seriesRef.current.flatMap(s => s.points.map(p => p.dist_ft));
      if (!allDists.length) return;
      const xMin = zoom ? zoom[0] : arrayMin(allDists);
      const xMax = zoom ? zoom[1] : arrayMax(allDists);
      if (xMax === xMin) return;

      const rightPx = rhs(selectedChannelsRef.current.length, splitActiveRef.current);
      const chartW  = wrap.clientWidth - 34 - rightPx;
      const t       = Math.max(0, Math.min(1, (distFt - xMin) / (xMax - xMin)));
      const xPx     = 34 + t * chartW;

      if (line) { line.style.left = `${xPx}px`; line.style.display = 'block'; }

      const entries = seriesRef.current.map(s => {
        const pt = findNearest(s.points, distFt);
        if (!pt) return null;
        return {
          seriesKey: s.seriesKey, lapLabel: s.lapLabel, color: s.color,
          speed: pt.speed, lat: pt.lat, lon: pt.lon, video_time: pt.video_time,
          lap_time: pt.lap_time, csv_time: pt.csv_time,
          ...channelValuesAt(channelDataRef.current, selectedChannelsRef.current, s.lapIndex, distFt, unitPrefsRef.current, seriesSource(s.seriesKey)),
        };
      }).filter(Boolean);
      setTooltipData(entries.length ? { x: xPx, distFt, entries } : null);
    };

    window.addEventListener('trackapp:chart-hover', handler);
    // Late mount (mobile tab switch): show the last cursor position.
    const last = lastCursor();
    if (last) handler({ detail: last });
    return () => window.removeEventListener('trackapp:chart-hover', handler);
  }, []);

  // Video playback → move orange playhead line + update map crosshair
  useEffect(() => {
    const handler = (e) => {
      const line = playheadLineRef.current;
      const wrap = chartWrapRef.current;
      if (!line || !wrap) return;

      const videoTime = e.detail?.videoTime;
      if (videoTime == null) { line.style.display = 'none'; return; }

      let nearest1 = null, minDiff1 = Infinity;
      let nearest2 = null, minDiff2 = Infinity;
      for (const s of seriesRef.current) {
        const isS2 = s.seriesKey.startsWith('s2-');
        for (const p of s.points) {
          const t = p.csv_time;
          if (t == null) continue;
          const diff = Math.abs(t - videoTime);
          if (!isS2 && diff < minDiff1) { minDiff1 = diff; nearest1 = p; }
          if (isS2  && diff < minDiff2) { minDiff2 = diff; nearest2 = p; }
        }
      }
      const nearest = nearest1 || nearest2;
      if (!nearest) { line.style.display = 'none'; return; }

      const zoom = zoomDomainRef.current;
      if (zoom && (nearest.dist_ft < zoom[0] || nearest.dist_ft > zoom[1])) {
        line.style.display = 'none';
      } else {
        const allDists = seriesRef.current.flatMap(s => s.points.map(p => p.dist_ft));
        if (!allDists.length) { line.style.display = 'none'; return; }
        const xMin = zoom ? zoom[0] : arrayMin(allDists);
        const xMax = zoom ? zoom[1] : arrayMax(allDists);
        if (xMax === xMin) { line.style.display = 'none'; return; }

        const rightPx = rhs(selectedChannelsRef.current.length, splitActiveRef.current);
        const chartW  = wrap.clientWidth - 34 - rightPx;
        const t       = Math.max(0, Math.min(1, (nearest.dist_ft - xMin) / (xMax - xMin)));
        line.style.left    = `${34 + t * chartW}px`;
        line.style.display = 'block';
      }

      if (nearest1?.lat && nearest1?.lon || nearest2?.lat && nearest2?.lon) {
        window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
          detail: {
            lat: nearest1?.lat, lon: nearest1?.lon,
            lat2: nearest2?.lat, lon2: nearest2?.lon,
            distFt: nearest?.dist_ft, videoTime, fromVideo: true,
          },
        }));
      }
    };

    window.addEventListener('trackapp:video-time', handler);
    return () => window.removeEventListener('trackapp:video-time', handler);
  }, []);

  // ── Zoom drag handlers ────────────────────────────────────────────────────
  // Mousedown only records a pending drag start — it must NOT touch React
  // state. Setting state here forces a re-render between mousedown and
  // mouseup, which can replace the DOM node under the cursor (Recharts
  // reshuffles its internal z-index layers on re-render) and makes the
  // browser silently drop the native 'click' event for what the user meant
  // as a plain click. handleMouseMove only promotes this to a real
  // zoom-drag once movement crosses DRAG_ARM_PX.
  const handleMouseDown = (e, event) => {
    const clientX = event?.clientX;
    const x = clientX != null
      ? pixelToDistFtRef.current(clientX)
      : (e?.activeLabel != null ? Number(e.activeLabel) : null);
    if (x == null) return;
    pendingDragRef.current = { x, clientX: clientX ?? null };
  };

  const finalizeZoom = () => {
    pendingDragRef.current = null;
    if (!isSelectingRef.current) return;
    isSelectingRef.current = false;
    setIsSelecting(false);

    const l = refAreaLeft.current;
    const r = refAreaRight.current;
    setSelectionViz(null);
    refAreaLeft.current  = null;
    refAreaRight.current = null;

    if (l == null || r == null) return;
    const [lo, hi] = l <= r ? [l, r] : [r, l];
    if (lo === hi) return;

    if (isAnnotateModeRef.current) {
      window.dispatchEvent(new CustomEvent('trackapp:annotation-captured', {
        detail: { channel: 'speed', channelLabel: 'Speed', distFtStart: lo, distFtEnd: hi },
      }));
      setIsAnnotateMode(false);
      isAnnotateModeRef.current = false;
      window.dispatchEvent(new CustomEvent('trackapp:annotate-mode', { detail: { active: false } }));
    }
    // publishZoom notifies all subscribers (including this component via subscribeZoom).
    // The window event keeps any non-bus listeners in sync.
    publishZoom([lo, hi]);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
      detail: { distFtMin: lo, distFtMax: hi },
    }));
  };
  finalizeZoomRef.current = finalizeZoom;

  const resetZoom = () => {
    publishZoom(null);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', { detail: null }));
  };

  const dispatchZoom = (domain) => {
    publishZoom(domain);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
      detail: { distFtMin: domain[0], distFtMax: domain[1] },
    }));
  };

  // Chart mouse move — compute tooltip imperatively, dispatch map crosshair
  const handleMouseMove = (data, event) => {
    isDirectHoverRef.current = true;

    // A mousedown is pending but not yet armed as a drag — promote it once
    // real movement is seen; below the threshold, do nothing here so a
    // plain click causes no state update at all.
    if (pendingDragRef.current && !isSelectingRef.current) {
      const start = pendingDragRef.current;
      const moved = start.clientX != null && event?.clientX != null
        ? Math.abs(event.clientX - start.clientX)
        : DRAG_ARM_PX; // no clientX available — arm immediately (safe default)
      if (moved < DRAG_ARM_PX) return;
      isSelectingRef.current = true;
      setIsSelecting(true);
      refAreaLeft.current  = start.x;
      refAreaRight.current = start.x;
      setSelectionViz({ left: start.x, right: start.x });
    }

    // While drag-selecting a zoom region, update the selection highlight
    if (isSelectingRef.current) {
      if (data?.activeLabel != null) {
        refAreaRight.current = Number(data.activeLabel);
        setSelectionViz({ left: refAreaLeft.current, right: refAreaRight.current });
      }
      return;
    }

    const coord  = data?.activeCoordinate;
    // activeLabel is the x-axis value (dist_ft) at the cursor
    const distFt = data?.activeLabel != null ? Number(data.activeLabel) : null;
    if (!coord || distFt == null || !seriesRef.current.length) {
      setTooltipData(null);
      return;
    }

    const entries = seriesRef.current.map(s => {
      const pt = findNearest(s.points, distFt);
      if (!pt) return null;
      return {
        seriesKey: s.seriesKey, lapLabel: s.lapLabel, color: s.color,
        speed: pt.speed, lat: pt.lat, lon: pt.lon, video_time: pt.video_time,
        lap_time: pt.lap_time, csv_time: pt.csv_time,
        ...channelValuesAt(channelData, selectedChannels, s.lapIndex, distFt, unitPrefs, seriesSource(s.seriesKey)),
      };
    }).filter(Boolean);

    const ww = chartWrapRef.current?.clientWidth ?? 600;
    const frac = coord.x / ww;
    if (frac < 0.45) tooltipSideRef.current = 'right';
    else if (frac > 0.55) tooltipSideRef.current = 'left';
    setTooltipData({ x: coord.x, distFt, entries, side: tooltipSideRef.current });

    // Use first series for map/video crosshair position
    const first  = entries.find(e => !e.seriesKey.startsWith('s2-')) || entries[0];
    const second = entries.find(e => e.seriesKey.startsWith('s2-'));
    if (first?.lat && first?.lon || second?.lat && second?.lon) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
        detail: {
          lat: first?.lat, lon: first?.lon,
          lat2: second?.lat, lon2: second?.lon,
          distFt,
          videoTime: first?.csv_time ?? null,
        },
      }));
    }
  };

  const handleMouseLeave = () => {
    isDirectHoverRef.current = false;
    pendingDragRef.current = null;
    if (isSelectingRef.current) {
      // Cancel selection when mouse leaves chart area
      isSelectingRef.current = false;
      setIsSelecting(false);
      setSelectionViz(null);
      refAreaLeft.current  = null;
      refAreaRight.current = null;
    }
    setTooltipData(null);
    window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail: null }));
  };

  const handleClick = (data, event) => {
    // Recharts' activeLabel can lag or reflect a synced chart's last hover
    // position rather than the actual click (no click-trigger tooltip is
    // rendered here). Derive the position from the click's own clientX instead.
    const clientX = event?.clientX;
    const distFt = clientX != null
      ? pixelToDistFtRef.current(clientX)
      : (data?.activeLabel != null ? Number(data.activeLabel) : null);
    if (distFt == null || !seriesRef.current.length) return;
    const first = seriesRef.current.map(s => {
      const pt = findNearest(s.points, distFt);
      return pt ? { lat: pt.lat, lon: pt.lon, csv_time: pt.csv_time } : null;
    }).filter(Boolean)[0];
    if (first?.lat && first?.lon) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-click', {
        detail: { lat: first.lat, lon: first.lon, distFt, videoTime: first.csv_time ?? null },
      }));
    }
  };

  const isEmpty = series.length === 0;

  const allSpeedDists = series.flatMap(s => s.points.map(p => p.dist_ft));
  const xDomain = zoomDomain || arrayExtent(allSpeedDists);

  // Lap distance in ft for sector-frac → dist_ft conversion.
  // Must be computed from raw lapTraces (not the zoomed series) so that sector
  // boundary positions stay correct even when the user has zoomed in.
  const lapTotalFt = useMemo(() => {
    let maxFt = 0;
    selectedLaps.forEach(lapIndex => {
      if (lapIndex === TB_LAP_INDEX) return;
      const trace = lapTraces[lapIndex];
      if (!trace?.points?.length) return;
      trace.points.forEach(p => {
        if (p.dist_m != null) maxFt = Math.max(maxFt, p.dist_m * M_TO_FT);
      });
    });
    return maxFt > 0 ? maxFt : null;
  }, [lapTraces, selectedLaps]);

  // ── Sector divider geometry / drag state ───────────────────────────────────
  // Internal sectors are stored as fractions; we render them at fixed dist_ft
  // positions on the chart. Dragging a divider updates fractions.
  // Natural (full-lap) x-extent — kept in a ref so the pan handler can clamp
  const naturalExtentRef = useRef(null);

  // Right margin varies with selectedChannels — keep a ref so usePanOnWheel
  // always reads the current value without re-registering the wheel listener
  const rightMarginRef = useRef(12);
  rightMarginRef.current = rhs(selectedChannels.length, splitActive);

  // Pan delta handler — shifts zoomDomain by deltaFt, clamped to natural extent
  const onPanFt = (deltaFt) => {
    const zoom = zoomDomainRef.current;
    if (!zoom) return;
    const nat    = naturalExtentRef.current;
    const natMin = nat ? nat[0] : zoom[0];
    const natMax = nat ? nat[1] : zoom[1];
    const size   = zoom[1] - zoom[0];
    let lo = zoom[0] + deltaFt;
    let hi = zoom[1] + deltaFt;
    if (lo < natMin) { hi = natMin + size; lo = natMin; }
    if (hi > natMax) { lo = natMax - size; hi = natMax; }
    dispatchZoom([lo, hi]);
  };
  const onPanFtRef = useRef(onPanFt);
  onPanFtRef.current = onPanFt;

  const onPinchZoom = (scaleFactor, centerFt) => {
    const nat    = naturalExtentRef.current;
    const zoom   = zoomDomainRef.current;
    const natMin = nat ? nat[0] : null;
    const natMax = nat ? nat[1] : null;
    let lo = zoom ? zoom[0] : natMin;
    let hi = zoom ? zoom[1] : natMax;
    if (lo == null || hi == null) return;
    const center = centerFt != null ? Math.max(lo, Math.min(hi, centerFt)) : (lo + hi) / 2;
    const newLo  = Math.max(natMin ?? lo, center - (center - lo) / scaleFactor);
    const newHi  = Math.min(natMax ?? hi, center + (hi - center) / scaleFactor);
    if (newHi - newLo < 200) return;
    dispatchZoom([newLo, newHi]);
  };
  const onPinchZoomRef = useRef(onPinchZoom);
  onPinchZoomRef.current = onPinchZoom;

  usePanOnWheel({ chartWrapRef, zoomDomainRef, leftPad: 34, rightPadRef: rightMarginRef, onPanFtRef });

  const draggingDividerRef = useRef(null);  // { boundaryIdx } during drag
  const sectorDragOriginRef = useRef(null);

  // Convert chart pixel x → dist_ft (inverse of the chart's x-scale).
  const pixelToDistFt = (clientX) => {
    const wrap = chartWrapRef.current;
    if (!wrap || !xDomain) return null;
    const rect = wrap.getBoundingClientRect();
    const leftPad = 34;
    const rightPad = rhs(selectedChannels.length, splitActive);
    const chartW = rect.width - leftPad - rightPad;
    if (chartW <= 0) return null;
    const t = Math.max(0, Math.min(1, (clientX - rect.left - leftPad) / chartW));
    return xDomain[0] + t * (xDomain[1] - xDomain[0]);
  };
  // Minimum on-screen gap between sector boundaries, in lap-fraction terms —
  // derived from a fixed pixel gap so it means the same thing on screen
  // regardless of track length or zoom level. 60px clears the sector-label
  // hit-box (24px half-width) plus the divider hit-box (4px half-width) on
  // both sides, so a label can never fully cover a divider/delete-X.
  const MIN_DIVIDER_PX = 60;
  const getMinGapFrac = () => {
    const wrap = chartWrapRef.current;
    if (!wrap || !xDomain || !lapTotalFt) return 0.005;
    const leftPad = 34;
    const rightPad = rhs(selectedChannels.length, splitActive);
    const chartW = wrap.clientWidth - leftPad - rightPad;
    if (chartW <= 0) return 0.005;
    const ftPerPx = (xDomain[1] - xDomain[0]) / chartW;
    return Math.max(0.005, (MIN_DIVIDER_PX * ftPerPx) / lapTotalFt);
  };

  // Keep a ref so the touch-handler useEffect (registered once) always calls
  // the latest version of pixelToDistFt, which closes over current xDomain.
  const pixelToDistFtRef = useRef(pixelToDistFt);
  pixelToDistFtRef.current = pixelToDistFt;

  // Sector boundary x-positions in dist_ft (one per non-zero/non-one frac).
  const sectorBoundariesFt = useMemo(() => {
    if (!sectors || !lapTotalFt) return [];
    // Boundaries are end_frac of each sector except the last (always 1.0).
    return sectors.slice(0, -1).map(s => s.end_frac * lapTotalFt);
  }, [sectors, lapTotalFt]);

  // Apply a new boundary list (in fracs) → reconstruct sectors.
  // Preserves existing labels/ids by index when the sector count stays the same.
  const applyBoundaryFracs = (fracs) => {
    if (!onSectorsChange) return;
    const sorted = [...fracs].filter(f => f > 0 && f < 1).sort((a, b) => a - b);
    const cuts = [0, ...sorted, 1];
    const newSectors = [];
    for (let i = 0; i < cuts.length - 1; i++) {
      const existing = sectors && sectors[i];
      newSectors.push({
        id:         existing ? existing.id    : `S${i + 1}`,
        label:      existing ? existing.label : `Sector ${i + 1}`,
        start_frac: cuts[i],
        end_frac:   cuts[i + 1],
      });
    }
    onSectorsChange(newSectors);
  };

  const handleDividerMouseDown = (boundaryIdx) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    draggingDividerRef.current = boundaryIdx;
  };

  // Global mousemove / mouseup for divider drag.
  useEffect(() => {
    if (!showSectorEditor) return;
    const onMove = (e) => {
      if (draggingDividerRef.current == null) return;
      const distFt = pixelToDistFt(e.clientX);
      if (distFt == null || !lapTotalFt) return;
      const idx = draggingDividerRef.current;
      const fracs = sectors.slice(0, -1).map(s => s.end_frac);
      const gap = getMinGapFrac();
      const lo = idx > 0 ? fracs[idx - 1] + gap : gap;
      const hi = idx < fracs.length - 1 ? fracs[idx + 1] - gap : 1 - gap;
      const newFrac = Math.max(lo, Math.min(hi, distFt / lapTotalFt));
      fracs[idx] = newFrac;
      applyBoundaryFracs(fracs);
    };
    const onUp = () => { draggingDividerRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSectorEditor, sectors, lapTotalFt, xDomain, selectedChannels, splitActive]);

  const allSpeeds = series.flatMap(s => s.points.map(p => p.speed));
  const yMin = allSpeeds.length ? Math.floor(arrayMin(allSpeeds) / 10) * 10 : 0;
  const yMax = allSpeeds.length ? Math.ceil( arrayMax(allSpeeds) / 10) * 10 : 160;

  // margin.right for the speed chart — the channel YAxes Recharts stacks beyond it
  // are counted separately by rhs(). Split mode has no right-hand axes at all.
  const rightMargin = splitActive ? 12
    : selectedChannels.length === 2 ? 76 : selectedChannels.length === 1 ? 44 : 12;

  const channelDomains = useMemo(() => {
    const domains = {};
    for (const ch of selectedChannels) {
      const allVals = Object.values(channelData[ch.key] || {})
        .flatMap(pts => pts.map(p => convertValue(p.value, ch.unit, unitPrefs)));
      domains[ch.key] = chDomain(allVals);
    }
    return domains;
  }, [selectedChannels, channelData, unitPrefs]);

  const toggleChannel = useCallback((ch) => {
    setSelectedChannels(prev => {
      const exists = prev.some(c => c.key === ch.key);
      if (exists) return prev.filter(c => c.key !== ch.key);
      if (prev.length < 2) return [...prev, ch];
      return [prev[1], ch]; // drop oldest, add new
    });
  }, []);

  // ── PNG export — chart SVG + title band + driver/lap legend + gap callouts ─
  const handleExportPng = useCallback(() => {
    const chartSvgs = [...(chartWrapRef.current?.querySelectorAll('svg.recharts-surface') || [])];
    const svg = chartSvgs[0] || chartWrapRef.current?.querySelector('svg');
    if (!svg || !series.length) return;
    const exportSvgs = splitActive && chartSvgs.length > 1 ? chartSvgs : [svg];
    const nameFor = (s) => {
      if (s.seriesKey.startsWith('s1-')) return driverName1;
      const slot = s.seriesKey.match(/^s2-(\d+)-/)?.[1];
      return compareDrivers.find(d => String(d.slot) === slot)?.name || 'Driver';
    };
    const dateFor = (s) => {
      if (s.seriesKey.startsWith('s1-')) return sessionDate;
      const slot = s.seriesKey.match(/^s2-(\d+)-/)?.[1];
      return compareDrivers.find(d => String(d.slot) === slot)?.session?.meta?.date || null;
    };
    // When one driver has several laps plotted, the name alone is ambiguous in
    // the gap callouts — qualify it with the lap number.
    const nameCounts = {};
    series.forEach(s => { nameCounts[nameFor(s)] = (nameCounts[nameFor(s)] || 0) + 1; });
    const gapName = (s) => nameCounts[nameFor(s)] > 1 ? `${nameFor(s)} L${s.lapNumber}` : nameFor(s);

    const vehicleFor = (s) => {
      if (s.seriesKey.startsWith('s1-')) return sessionVehicle;
      const slot = s.seriesKey.match(/^s2-(\d+)-/)?.[1];
      return compareDrivers.find(d => String(d.slot) === slot)?.session?.meta?.vehicle || null;
    };
    // Dates/vehicles go in the title or are omitted when uniform, and move into
    // each legend entry when they differ between the plotted sessions.
    const mixedDates = new Set(series.map(dateFor).filter(Boolean)).size > 1;
    const mixedVehicles = new Set(series.map(vehicleFor).filter(Boolean)).size > 1;

    // Gap to the fastest plotted real lap, from the lap-time string (TB excluded).
    const lapSeconds = (s) => {
      if (s.isTb || !s.lapLabel || !s.lapLabel.includes('.')) return null;
      const parts = s.lapLabel.split(':');
      const t = parts.length === 2 ? parseFloat(parts[0]) * 60 + parseFloat(parts[1]) : parseFloat(s.lapLabel);
      return Number.isFinite(t) ? t : null;
    };
    const bestSec = Math.min(...series.map(s => lapSeconds(s) ?? Infinity));

    const legend = series.map(s => {
      // lapLabel is the lap-time string when known, else "L<n>" — avoid "L3 · L3".
      const lapBit = s.isTb || s.lapLabel === `L${s.lapNumber}`
        ? s.lapLabel : `L${s.lapNumber} · ${s.lapLabel}`;
      const sec = lapSeconds(s);
      const deltaBit = sec != null && Number.isFinite(bestSec) && sec > bestSec
        ? ` (+${(sec - bestSec).toFixed(3)})` : '';
      return {
        color: s.color,
        label: `${nameFor(s)} — ${lapBit}${deltaBit}${
          mixedVehicles && vehicleFor(s) ? ` · ${vehicleFor(s)}` : ''}${
          mixedDates && dateFor(s) ? ` · ${dateFor(s)}` : ''}`,
      };
    });

    // ── Largest-gap callouts: sample the plotted domain, find where the speed
    // spread across lines peaks (top 3, spaced apart). TB is synthetic — skip it.
    const realSeries = series.filter(s => !s.isTb && s.points.length > 1);
    let annotations = [];
    if (realSeries.length >= 2) {
      const x0 = zoomDomain?.[0] ?? Math.min(...realSeries.map(s => s.points[0].dist_ft));
      const x1 = zoomDomain?.[1] ?? Math.max(...realSeries.map(s => s.points[s.points.length - 1].dist_ft));
      const N = 240;
      const step = (x1 - x0) / N;
      const speedAt = (s, x) => {
        const pts = s.points;
        let lo = 0, hi = pts.length - 1;
        while (lo < hi) { const mid = (lo + hi) >> 1; if (pts[mid].dist_ft < x) lo = mid + 1; else hi = mid; }
        const best = (lo > 0 && Math.abs(pts[lo - 1].dist_ft - x) < Math.abs(pts[lo].dist_ft - x))
          ? pts[lo - 1] : pts[lo];
        return Math.abs(best.dist_ft - x) <= step * 2 ? best.speed : null;
      };
      const samples = [];
      for (let k = 0; k <= N; k++) {
        const x = x0 + k * step;
        let fast = null, slow = null;
        for (const s of realSeries) {
          const v = speedAt(s, x);
          if (v == null) continue;
          if (!fast || v > fast.v) fast = { v, s };
          if (!slow || v < slow.v) slow = { v, s };
        }
        if (fast && slow && fast.s !== slow.s) samples.push({ x, spread: fast.v - slow.v, fast, slow });
      }
      const maxSpeed = Math.max(...samples.map(c => c.fast.v), 1);
      const minSpread = Math.max(3, maxSpeed * 0.04);   // ignore noise-level gaps
      const minSep = (x1 - x0) * 0.12;                  // keep callouts apart
      const picked = [];
      for (const c of [...samples].sort((a, b) => b.spread - a.spread)) {
        if (c.spread < minSpread || picked.length === 3) break;
        if (picked.some(p => Math.abs(p.x - c.x) < minSep)) continue;
        picked.push(c);
      }
      picked.sort((a, b) => a.x - b.x);

      // Pixel mapping — same plot geometry as the sector-editor overlay.
      const svgW = svg.getBoundingClientRect().width;
      const leftPad = 34;
      const chartW = svgW - leftPad - rhs(selectedChannels.length, splitActive);
      const distUnit = getDistanceUnit(unitPrefs);
      const fmtDist = (ft) => {
        const v = convertDistanceFt(ft, unitPrefs);
        return v >= 1000 ? `${(v / 1000).toFixed(1)}k ${distUnit}` : `${Math.round(v)} ${distUnit}`;
      };
      const speedUnit = getDisplayUnit('mph', unitPrefs);
      annotations = picked.map((c, i) => ({
        num: i + 1,
        xPx: leftPad + ((c.x - x0) / (x1 - x0)) * chartW,
        text: `at ${fmtDist(c.x)}: ${gapName(c.slow.s)} ${Math.round(c.slow.v)} vs `
            + `${gapName(c.fast.s)} ${Math.round(c.fast.v)} ${speedUnit} (Δ ${c.spread.toFixed(1)})`,
      }));
    }

    const slug = v => (v || '').replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '');
    const host = window.location.hostname;
    exportChartPng({
      svg,
      svgs: exportSvgs,
      themeFrom: chartWrapRef.current,
      title: [trackName,
              mixedVehicles ? null : sessionVehicle,
              mixedDates ? null : sessionDate].filter(Boolean).join(' · ') || 'Speed Trace',
      subtitle: splitActive
        ? `${['Speed', ...selectedChannels.map(channel => channel.name)].join(' · ')} vs Distance`
        : `Speed vs Distance (${getDisplayUnit('mph', unitPrefs)})`,
      legend,
      annotations,
      plotTop: CHART_TOP,
      plotBottom: CHART_BOTTOM,
      // Gap callouts describe speed only, so stop their marker lines at the
      // bottom of the first sub-chart rather than drawing through the channels.
      plotEnd: splitActive ? svg.getBoundingClientRect().height - CHART_BOTTOM : null,
      watermark: /^(localhost|127\.|\d+\.\d+\.)/.test(host) ? 'TrackDay Analysis' : host,
      filename: `${[slug(trackName) || 'track', slug(sessionDate), 'speedtrace'].filter(Boolean).join('_')}.png`,
    }).catch(err => console.error('Chart export failed:', err));
  }, [series, compareDrivers, driverName1, trackName, sessionDate, sessionVehicle,
      unitPrefs, zoomDomain, selectedChannels, splitActive]);

  // ── Chart pieces shared by the overlay chart and every split sub-chart ─────
  const chartEvents = {
    onMouseDown:  handleMouseDown,
    onMouseMove:  handleMouseMove,
    onMouseUp:    finalizeZoom,
    onMouseLeave: handleMouseLeave,
    onClick:      handleClick,
  };

  const gridEl = <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />;

  // `visible` false keeps the identical 20-px axis slot but paints nothing, so
  // upper sub-charts stay aligned with the labelled one at the bottom.
  const xAxisEl = (visible) => (
    <XAxis
      dataKey="dist_ft"
      type="number"
      domain={xDomain || ['auto', 'auto']}
      tickFormatter={v => `${(convertDistanceFt(v, unitPrefs) / 1000).toFixed(1)}k`}
      stroke={visible ? chartColors.axis : 'transparent'}
      tick={{ fill: visible ? chartColors.tick : 'transparent', fontSize: 10 }}
      tickLine={visible}
      axisLine={visible}
      height={20}
      allowDuplicatedCategory={false}
      label={visible
        ? { value: `${getDistanceUnit(unitPrefs)} from lap start`, position: 'insideBottomRight', offset: -4, fill: chartColors.label, fontSize: 9 }
        : undefined}
    />
  );

  const speedYAxisEl = (
    <YAxis
      yAxisId="speed"
      domain={[yMin, yMax]}
      stroke={chartColors.axis}
      tick={{ fill: chartColors.tick, fontSize: 10 }}
      label={{ value: getDisplayUnit('mph', unitPrefs), angle: -90, position: 'insideLeft', offset: 14, fill: chartColors.label, fontSize: 10 }}
      width={34}
    />
  );

  // Channel YAxis — right-hand stack in overlay mode, its own left axis in split.
  const channelYAxisEl = (ch, idx) => {
    const displayUnit = getDisplayUnit(ch.unit, unitPrefs) || ch.unit;
    return (
      <YAxis
        key={ch.key}
        yAxisId={ch.key}
        orientation={splitActive ? 'left' : 'right'}
        domain={channelDomains[ch.key] || ['auto', 'auto']}
        stroke={chartColors.axis}
        tick={{ fill: chartColors.tick, fontSize: 10 }}
        tickFormatter={fmtTick}
        label={splitActive
          ? { value: displayUnit || ch.name, angle: -90, position: 'insideLeft', offset: 14, fill: chartColors.label, fontSize: 10 }
          : { value: displayUnit || ch.name, angle: 90, position: 'insideRight', offset: idx === 0 ? 10 : 44, fill: chartColors.label, fontSize: 10 }}
        width={34}
      />
    );
  };

  const speedLineEls = series.map(({ seriesKey, lapLabel, color, points, isTb }) => (
    <Line
      key={seriesKey}
      yAxisId="speed"
      data={points}
      dataKey="speed"
      name={lapLabel}
      stroke={color}
      strokeWidth={isTb ? 2 : 1.5}
      strokeDasharray={isTb ? '6 4' : undefined}
      dot={false}
      isAnimationActive={false}
      activeDot={false}
    />
  ));

  // Channel overlays — each channel's line only pairs with the series belonging
  // to its own driver (source), so a compare driver's channel never draws against
  // the primary's line (or another driver's) even if lap indices coincide.
  // In split mode the channel owns its chart, so it needs no dash to be told
  // apart and is drawn solid at full opacity.
  const channelLineEls = (ch, idx) => {
    const stroke = CH_STROKES[idx] || CH_STROKES[0];
    return series.filter(s => seriesSource(s.seriesKey) === ch.source).map(s => {
      const raw = channelData[ch.key]?.[s.lapIndex];
      if (!raw?.length) return null;
      const pts = raw
        .filter(p => !zoomDomain || (p.dist_ft >= zoomDomain[0] && p.dist_ft <= zoomDomain[1]))
        .map(p => ({ dist_ft: p.dist_ft, value: convertValue(p.value, ch.unit, unitPrefs) }));
      if (!pts.length) return null;
      // Digital/boolean channels (0/1) log discrete state changes, not a
      // continuous ramp — draw them as a square wave.
      const isBoolean = pts.every(p => p.value === 0 || p.value === 1);
      return (
        <Line
          key={`${ch.key}-${s.seriesKey}`}
          yAxisId={ch.key}
          data={pts}
          dataKey="value"
          name={`${ch.name}-${s.seriesKey}`}
          type={isBoolean ? 'stepAfter' : undefined}
          stroke={s.color}
          strokeWidth={splitActive ? 1.5 : 1.25}
          strokeDasharray={splitActive ? (s.isTb ? '6 4' : undefined) : stroke.strokeDasharray}
          strokeOpacity={splitActive ? 1 : stroke.strokeOpacity}
          dot={false}
          isAnimationActive={false}
          connectNulls={false}
          legendType="none"
        />
      );
    });
  };

  const speedDotEls = () => (showTopSpeed ? series.flatMap(({ seriesKey, color, maxPt, minPt }) => [
    <ReferenceDot
      key={`max-${seriesKey}`}
      yAxisId="speed"
      x={maxPt.dist_ft}
      y={maxPt.speed}
      r={5}
      fill={color}
      stroke="#fff"
      strokeWidth={1.5}
      label={{
        value: `${Math.round(maxPt.speed)}`,
        position: 'bottom',
        fill: color, fontSize: 10, fontWeight: 700,
      }}
    />,
    <ReferenceDot
      key={`min-${seriesKey}`}
      yAxisId="speed"
      x={minPt.dist_ft}
      y={minPt.speed}
      r={5}
      fill={color}
      stroke="#fff"
      strokeWidth={1.5}
      label={{
        value: `${Math.round(minPt.speed)}`,
        position: 'top',
        fill: color, fontSize: 10, fontWeight: 700,
      }}
    />,
  ]) : []);

  // Min/Max channel dots — same driver-scoping as the overlay lines.
  const channelDotEls = (ch) => (showTopSpeed
    ? series.filter(s => seriesSource(s.seriesKey) === ch.source).flatMap(s => {
        const raw = channelData[ch.key]?.[s.lapIndex];
        if (!raw?.length) return [];
        const visible = raw.filter(p => !zoomDomain || (p.dist_ft >= zoomDomain[0] && p.dist_ft <= zoomDomain[1]));
        if (!visible.length) return [];
        const conv = (v) => convertValue(v, ch.unit, unitPrefs);
        const maxPt = visible.reduce((b, p) => conv(p.value) > conv(b.value) ? p : b, visible[0]);
        const minPt = visible.reduce((b, p) => conv(p.value) < conv(b.value) ? p : b, visible[0]);
        return [
          <ReferenceDot
            key={`max-${ch.key}-${s.seriesKey}`}
            yAxisId={ch.key}
            x={maxPt.dist_ft} y={conv(maxPt.value)}
            r={5} fill={s.color} stroke="#fff" strokeWidth={1.5}
            label={{ value: fmtVal(conv(maxPt.value)), position: 'bottom', fill: s.color, fontSize: 10, fontWeight: 700 }}
          />,
          <ReferenceDot
            key={`min-${ch.key}-${s.seriesKey}`}
            yAxisId={ch.key}
            x={minPt.dist_ft} y={conv(minPt.value)}
            r={5} fill={s.color} stroke="#fff" strokeWidth={1.5}
            label={{ value: fmtVal(conv(minPt.value)), position: 'top', fill: s.color, fontSize: 10, fontWeight: 700 }}
          />,
        ];
      })
    : []);

  // Sector background bands — always visible when enabled, independent of the
  // Sectors editor toggle, colored to match TrackMap's Sector color mode so the
  // two views read as one system.
  const sectorBandEls = (yAxisId) => (showSectorBands && lapTotalFt
    ? (sectors || []).map((s, i) => (
        <ReferenceArea
          key={`sector-bg-${s.id}`}
          yAxisId={yAxisId}
          x1={s.start_frac * lapTotalFt}
          x2={s.end_frac * lapTotalFt}
          fill={LAP_COLORS[i % LAP_COLORS.length]}
          fillOpacity={0.10}
          stroke="none"
          ifOverflow="visible"
          label={{
            value: s.label, position: 'insideTop',
            fill: chartColors.label, fontSize: 9, fontWeight: 600,
          }}
        />
      ))
    : null);

  // Corner highlight from the Corner Metrics panel
  const cornerAreaEl = (yAxisId) => (cornerHl?.distFtMin != null ? (
    <ReferenceArea
      yAxisId={yAxisId}
      x1={cornerHl.distFtMin}
      x2={cornerHl.distFtMax}
      fill="#f5c842"
      fillOpacity={0.07}
      stroke="#f5c842"
      strokeOpacity={0.35}
      strokeDasharray="4 3"
    />
  ) : null);

  // Drag selection highlight — yellow for zoom, blue for annotate
  const selectionAreaEl = (yAxisId) => (selectionViz && selectionViz.left !== selectionViz.right ? (
    <ReferenceArea
      yAxisId={yAxisId}
      x1={Math.min(selectionViz.left, selectionViz.right)}
      x2={Math.max(selectionViz.left, selectionViz.right)}
      fill={isAnnotateMode ? '#4f9cf5' : '#f5c842'}
      fillOpacity={0.15}
      stroke={isAnnotateMode ? '#4f9cf5' : '#f5c842'}
      strokeOpacity={0.6}
    />
  ) : null);

  const splitChartMargin = { top: CHART_TOP, right: 12, bottom: 4, left: 0 };

  return (
    <div className={styles.panel} style={height != null ? { height, minHeight: height } : { height: '100%' }}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>Speed Trace</span>}>
        <div className={styles.opts}>
          <SpeedChannelPicker
            availableChannels={availableChannels}
            selectedChannels={selectedChannels}
            onToggle={toggleChannel}
          />
          <button
            className={styles.optBtn}
            onClick={handleExportPng}
            title={splitActive
              ? 'Download the full split chart as a PNG image with a driver / lap-time legend'
              : 'Download this chart as a PNG image with a driver / lap-time legend'}
          >PNG ⤓</button>
          {selectedChannels.length > 0 && (
            <button
              className={`${styles.optBtn} ${splitMode ? styles.optActive : ''}`}
              onClick={() => setSplitMode(v => {
                try { localStorage.setItem(SPLIT_KEY, v ? '0' : '1'); } catch { /* ignore */ }
                return !v;
              })}
              title="Give each selected channel its own chart under the speed trace, instead of overlaying it"
            >Split</button>
          )}
          <button
            className={`${styles.optBtn} ${showTopSpeed ? styles.optActive : ''}`}
            onClick={() => setShowTopSpeed(v => !v)}
          >Min/Max</button>
          {onSectorsChange && (
            <button
              className={`${styles.optBtn} ${showSectorEditor ? styles.optActive : ''}`}
              onClick={() => { setShowSectorEditor(v => !v); setEditingSectorId(null); }}
              title="Edit sectors — drag to move, click × to remove, click label to rename, click between dividers to add"
              style={showSectorEditor ? { color: '#FFD700' } : {}}
            >Sectors</button>
          )}
          <HelpButton panel="speedtrace" page="sessions" className={styles.optBtn} />
        </div>
      </PanelHeader>

      <div
        className={styles.chartWrap}
        ref={chartWrapRef}
        onDoubleClick={resetZoom}
        style={{
          position: 'relative',
          cursor: isSelecting
            ? (isAnnotateMode ? 'crosshair' : 'ew-resize')
            : (isAnnotateMode ? 'crosshair' : 'col-resize'),
          userSelect: 'none',
          outline: isAnnotateMode ? '2px solid #4f9cf5' : 'none',
          outlineOffset: '-2px',
        }}
      >
        {/* Sector dividers + add-on-click overlay */}
        {showSectorEditor && lapTotalFt && xDomain && (() => {
          const wrap = chartWrapRef.current;
          if (!wrap) return null;
          const leftPad = 34;
          const rightPad = rhs(selectedChannels.length, splitActive);
          const chartW = wrap.clientWidth - leftPad - rightPad;
          const xToPx = (distFt) => leftPad + chartW * (distFt - xDomain[0]) / (xDomain[1] - xDomain[0]);
          const handleAddClick = (e) => {
            const distFt = pixelToDistFt(e.clientX);
            if (distFt == null) return;
            const newFrac = Math.max(0.01, Math.min(0.99, distFt / lapTotalFt));
            const existing = sectors.slice(0, -1).map(s => s.end_frac);
            const gap = getMinGapFrac();
            // Avoid adding too close to an existing divider, or the lap start/end.
            if ([0, ...existing, 1].some(f => Math.abs(f - newFrac) < gap)) return;
            applyBoundaryFracs([...existing, newFrac]);
          };
          return (
            <div
              onClick={handleAddClick}
              style={{
                position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
                zIndex: 8, pointerEvents: 'auto', cursor: 'crosshair',
                background: 'rgba(255,215,0,0.04)',
              }}
            >
              {sectorBoundariesFt.map((distFt, i) => {
                const px = xToPx(distFt);
                if (px < leftPad - 4 || px > leftPad + chartW + 4) return null;
                return (
                  <div
                    key={i}
                    data-divider="1"
                    onMouseDown={handleDividerMouseDown(i)}
                    onClick={(e) => { e.stopPropagation(); }}
                    title={`Sector boundary ${i + 1} — drag to move`}
                    style={{
                      position: 'absolute',
                      left: px - 4,
                      top: 4,
                      bottom: 28,
                      width: 8,
                      zIndex: 10,
                      cursor: 'ew-resize',
                      background: 'transparent',
                    }}
                  >
                    {/* Yellow vertical line */}
                    <div style={{
                      position: 'absolute',
                      left: 3, top: 0, bottom: 0, width: 2,
                      background: '#FFD700', opacity: 0.9,
                      pointerEvents: 'none',
                    }} />
                    {/* × remove button */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        const fracs = sectors.slice(0, -1).map(s => s.end_frac);
                        fracs.splice(i, 1);
                        applyBoundaryFracs(fracs);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      title="Remove this sector boundary"
                      style={{
                        position: 'absolute',
                        left: -4, top: 0,
                        width: 16, height: 16,
                        borderRadius: '50%',
                        background: '#FFD700',
                        border: '1px solid #1a1300',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 900,
                        color: '#1a1300',
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        lineHeight: 1,
                      }}
                    >×</div>
                  </div>
                );
              })}
              {/* Sector labels — click to rename in editor mode */}
              {sectors && sectors.map((s) => {
                const cx = xToPx((s.start_frac + s.end_frac) / 2 * lapTotalFt);
                if (cx < leftPad - 20 || cx > leftPad + chartW + 20) return null;
                const isEditing = editingSectorId === s.id;
                if (isEditing) {
                  return (
                    <input
                      key={s.id}
                      autoFocus
                      defaultValue={s.label}
                      onBlur={(e) => {
                        const newLabel = e.target.value.trim() || s.label;
                        onSectorsChange(sectors.map(sec =>
                          sec.id === s.id ? { ...sec, label: newLabel } : sec
                        ));
                        setEditingSectorId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                        if (e.key === 'Escape') setEditingSectorId(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        left: cx - 40, top: 3,
                        width: 80,
                        fontSize: 9, color: '#FFD700',
                        background: '#1a1a00',
                        border: '1px solid #FFD700',
                        borderRadius: 3,
                        textAlign: 'center',
                        padding: '1px 3px',
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        zIndex: 20,
                      }}
                    />
                  );
                }
                return (
                  <div
                    key={s.id}
                    onClick={showSectorEditor ? (e) => {
                      e.stopPropagation();
                      setEditingSectorId(s.id);
                    } : undefined}
                    title={showSectorEditor ? `${s.label} — click to rename` : undefined}
                    style={{
                      position: 'absolute',
                      left: cx - 24, top: 6,
                      width: 48, textAlign: 'center',
                      fontSize: 9, color: '#FFD700', opacity: 0.8,
                      pointerEvents: showSectorEditor ? 'auto' : 'none',
                      fontWeight: 600,
                      zIndex: 1,
                      cursor: showSectorEditor ? 'text' : 'default',
                    }}
                  >{s.label.replace('Sector ', 'S')}</div>
                );
              })}
            </div>
          );
        })()}

        {/* Map-hover line — yellow */}
        <div
          ref={hoverLineRef}
          style={{
            display: 'none', position: 'absolute',
            top: 8, bottom: 4, width: 1,
            background: '#f5c842', opacity: 0.85,
            pointerEvents: 'none', zIndex: 10,
          }}
        />
        {/* Video playhead line — orange dashed */}
        <div
          ref={playheadLineRef}
          style={{
            display: 'none', position: 'absolute',
            top: 8, bottom: 4, width: 2,
            background: '#e8411a', opacity: 0.9,
            pointerEvents: 'none', zIndex: 11,
            borderLeft: '2px dashed #e8411a',
          }}
        />

        {/* Following dots — one per series at the hovered speed value */}
        <HoverDots
          data={tooltipData}
          yMin={yMin}
          yMax={yMax}
          wrapHeight={wrapHeight}
          channelDomains={channelDomains}
          bands={splitLayout?.bands || overlayBands}
        />

        {/* Imperative hover tooltip */}
        <HoverTooltip
          data={tooltipData}
          wrapWidth={chartWrapRef.current?.clientWidth}
          anchoredTooltip={anchoredTooltip}
          unitPrefs={unitPrefs}
        />

        {isEmpty ? (
          <div className={styles.empty}>
            Select laps to see speed traces
          </div>
        ) : splitActive ? (
          // ── Split mode: speed on top, one sub-chart per channel ─────────────
          // Explicit pixel sizes (not ResponsiveContainer) so the stack always
          // adds up to the wrapper height. wrapWidth is 0 until the first
          // ResizeObserver fire — skip rendering until measured.
          wrapWidth > 0 && (
            <>
              <div style={{ height: splitLayout.speedH, position: 'relative', overflow: 'hidden' }}>
                <LineChart
                  syncId="track" syncMethod="value" accessibilityLayer={false}
                  width={wrapWidth} height={splitLayout.speedH}
                  margin={splitChartMargin}
                  {...chartEvents}
                >
                  {gridEl}
                  {xAxisEl(false)}
                  {speedYAxisEl}
                  {speedLineEls}
                  {speedDotEls()}
                  {sectorBandEls('speed')}
                  {cornerAreaEl('speed')}
                  {selectionAreaEl('speed')}
                </LineChart>
              </div>

              {selectedChannels.map((ch, idx) => (
                <div key={ch.key}>
                  <div style={{ height: SPLIT_DIVIDER_H, background: chartColors.grid }} />
                  <div style={{ height: splitLayout.chH, position: 'relative', overflow: 'hidden' }}>
                    <LineChart
                      syncId="track" syncMethod="value" accessibilityLayer={false}
                      width={wrapWidth} height={splitLayout.chH}
                      margin={splitChartMargin}
                      {...chartEvents}
                    >
                      {gridEl}
                      {/* Only the bottom sub-chart carries the labelled x-axis */}
                      {xAxisEl(idx === selectedChannels.length - 1)}
                      {channelYAxisEl(ch, idx)}
                      {channelLineEls(ch, idx)}
                      {channelDotEls(ch)}
                      {cornerAreaEl(ch.key)}
                      {selectionAreaEl(ch.key)}
                    </LineChart>
                  </div>
                </div>
              ))}
            </>
          )
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              syncId="track"
              syncMethod="value"
              accessibilityLayer={false}
              margin={{ top: CHART_TOP, right: rightMargin, bottom: 4, left: 0 }}
              {...chartEvents}
            >
              {gridEl}
              {xAxisEl(true)}
              {/* Left axis — speed */}
              {speedYAxisEl}
              {selectedChannels.map((ch, idx) => channelYAxisEl(ch, idx))}

              {/* Speed lines */}
              {speedLineEls}

              {/* Channel overlays */}
              {selectedChannels.flatMap((ch, idx) => channelLineEls(ch, idx))}

              {/* Min/Max speed dots */}
              {speedDotEls()}

              {/* Min/Max channel dots */}
              {selectedChannels.flatMap(ch => channelDotEls(ch))}

              {sectorBandEls('speed')}
              {cornerAreaEl('speed')}
              {selectionAreaEl('speed')}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
